import { OperationStatus } from "@wallet-manager/node-types/dist/src/postgres/const";
import { Fragment } from "react";
import ChainNameSingleSelection from "src/components/ApproveAllowance/ChainNameSingleSelection";
import CurrencySingleSelection from "src/components/ApproveAllowance/CurrencySingleSelection";
import { NodeTypesEnumMultiSelection } from "src/components/GeneralSelection";
import MpTextField from "src/components/MpTextField";
import { InputAdornment, SelectChangeEvent } from "src/components/MuiGenerals";
import SpenderAddressSingleSelection from "src/components/SpenderAddress/SpenderAddressSingleSelection";
import PageConfigProvider, {
  usePageConfigProviderContext,
} from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import { separateBatchStrings } from "src/utils/helper";
import { genField } from "src/utils/HelperComp";
import { FilterFields, FilterFieldsKeys } from "../types/FilterFields";
import { TableColumnKey } from "../types/Table";

const useRenderFilters = () => {
  const { translatePrefix, fields, setFields } = usePageConfigProviderContext<
    FilterFields,
    unknown
  >();
  const { t, tc } = useTranslation(translatePrefix);

  const onChange =
    (type: FilterFieldsKeys) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const filterFields = genField({ t }, [
    [
      TableColumnKey.CreationTime,
      <PageConfigProvider.DateRangePicker
        type="dateTime"
        onDateRangePicked={(dateRange) => {
          setFields((fields) => ({ ...fields, creationTime: dateRange }));
        }}
      />,
    ],
    [
      TableColumnKey.LastModifiedTime,
      <PageConfigProvider.DateRangePicker
        type="dateTime"
        onDateRangePicked={(dateRange) => {
          setFields((fields) => ({ ...fields, lastModifiedTime: dateRange }));
        }}
      />,
    ],
    [
      TableColumnKey.ChainName,
      <ChainNameSingleSelection
        value={fields.chainName}
        fieldName={t(FilterFieldsKeys.ChainName)}
        onChange={(e: SelectChangeEvent<string>) => {
          setFields((f) => ({
            ...f,
            chainName: e.target.value,
            assetName: "",
            spenderAddress: "",
          }));
        }}
      />,
    ],
    [
      TableColumnKey.Currency,
      <CurrencySingleSelection
        value={fields.assetName}
        fieldName={t(TableColumnKey.Currency)}
        onChange={(e: SelectChangeEvent<string>) => {
          setFields((f) => ({
            ...f,
            assetName: e.target.value,
            spenderAddress: "",
          }));
        }}
        chainName={fields.chainName}
      />,
    ],
    [
      TableColumnKey.SpenderAddress,
      <SpenderAddressSingleSelection
        fieldName={t(FilterFieldsKeys.SpenderAddress)}
        value={fields.spenderAddress}
        onChange={(e: SelectChangeEvent<string>) =>
          setFields((f) => ({ ...f, spenderAddress: e.target.value }))
        }
        chainName={fields.chainName}
        currency={fields.assetName}
      />,
    ],
    [
      TableColumnKey.BatchId,
      <MpTextField
        label={tc("phInputField", {
          fieldName: `${t(TableColumnKey.BatchId)} (${tc(
            "multipleSelection",
          )})`,
        })}
        value={fields.batchId}
        onChange={(e) => {
          if (e.target.value === "") {
            return setFields((f) => ({ ...f, batchId: "" }));
          }
          const re = new RegExp("^[s]*[0-9]*$");
          const array = separateBatchStrings(e.target.value);
          const lastValue = array[array.length - 1];
          if (!re.test(lastValue)) {
            return;
          }
          setFields((f) => ({ ...f, batchId: e.target.value }));
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {separateBatchStrings(fields[FilterFieldsKeys.BatchId]).length}
            </InputAdornment>
          ),
        }}
      />,
    ],
    [
      TableColumnKey.Status,
      <NodeTypesEnumMultiSelection
        label={tc("phSelection", { fieldName: t(TableColumnKey.Status) })}
        value={fields.statuses}
        onChange={(statuses) => setFields((f) => ({ ...f, statuses }))}
        enumData={OperationStatus}
      />,
    ],
  ]);

  const renderFields = () =>
    Object.keys(filterFields).map((key, index) => (
      <Fragment key={index}>{filterFields[key]}</Fragment>
    ));

  return { renderFields };
};
export default useRenderFilters;
